import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { StyleConfigInterface } from './platforms/web/src/config/themeConfig/stylesInterface';
import { getStyleConfig, globalStyles } from './platforms/web/src/config/themeConfig/styles';
import { getFirebaseConfig } from './platforms/web/src/firebase';
import { branding_theme } from './platforms/web/src/config/themeConfig/branding';
import LoadingOverlay from './packages/ui-react/src/components/LoadingOverlay/LoadingOverlay';
import { useConfigStore } from './packages/common/src/stores/ConfigStore';

interface ThemeContextType {
  universalStyles: StyleConfigInterface;
  setUniversalStyles: React.Dispatch<React.SetStateAction<StyleConfigInterface>>;
  themeConfig: any;
  setThemeConfig: React.Dispatch<React.SetStateAction<any>>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [universalStyles, setUniversalStyles] = useState<StyleConfigInterface>(globalStyles);
  const [themeConfig, setThemeConfig] = useState<any>(branding_theme);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const config = await getFirebaseConfig();
        if (config) {
          setThemeConfig(config?.theme?.theme_colors);
          setUniversalStyles(
            getStyleConfig(config?.theme?.theme_colors, {
              primary_font: config?.appConfig?.branding?.primary_font,
              secondary_font: config?.appConfig?.branding?.secondary_font,
            }),
          );
          updateRootStyles(config?.theme?.theme_colors, config?.appConfig?.branding?.primary_font);
          useConfigStore.setState((s) => ({
            ...s,
            featureFlags: config?.theme,
            analyticsConfig: config?.analyticsConfig,
          }));
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {}, []);

  const updateRootStyles = (config: any, fontConfig: string) => {
    const root = document.documentElement;
    root.style.setProperty('--body-background-color', config?.background);
    root.style.setProperty('--highlight-color', config?.primary);
    root.style.setProperty('--primary-btn-color', config?.primary);
    root.style.setProperty('--primary-color', config?.neutral_white);
    root.style.setProperty('--card-color', config?.neutral_white);
    root.style.setProperty('--body-font-family', fontConfig);
    root.style.setProperty('--modal-background', config?.neutral_900);
    root.style.setProperty('--secondary-btn-color', config?.neutral_900);
    root.style.setProperty('--blue', config?.neutral_900);
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return <ThemeContext.Provider value={{ universalStyles, setUniversalStyles, themeConfig, setThemeConfig }}>{children}</ThemeContext.Provider>;
};
